import api from 'api';
import { AppDocument } from 'api';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { loadDocs } from 'redux/application/documents/documents-middleware';
import { RootState, AppDispatch } from 'redux/store';
import {
    Modal,
    ModalHeader,
    Form,
    Section,
    Actions,
    PrimaryButton,
    SecondaryButton,
} from 'components/common/Modal';
import { formAreaSx, longOptionsMenuProps } from '../eligibility/Eligibility.styled';

type CreateTicketModalProps = {
    open: boolean;
    onClose: () => void;
    onCreated?: () => void;
    mobile: boolean;
    /**
     * Pre-selected document id. When provided, the document dropdown is hidden
     * and the ticket is locked to this document.
     */
    documentId?: number;
    /**
     * Display name for the pre-selected document. Falls back to lookup in docsList.
     */
    documentName?: string;
};

export default function CreateTicketModal({
    open,
    onClose,
    onCreated,
    mobile,
    documentId,
    documentName,
}: CreateTicketModalProps) {
    const dispatch = useDispatch<AppDispatch>();
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const { entity: activeApp } = useSelector((state: RootState) => state.application);
    const { list: docsList } = useSelector((state: RootState) => state.documentsApp);

    const [selectedDocumentId, setSelectedDocumentId] = useState<string>('');
    const [ticketDetails, setTicketDetails] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const lockedDoc = docsList.find((doc: AppDocument) => doc.id === documentId);
    const lockedDocName =
        documentName || lockedDoc?.document_name || t('document', { lng: language });

    useEffect(() => {
        if (!open) {
            setSelectedDocumentId('');
            setTicketDetails('');
        } else if (documentId) {
            setSelectedDocumentId(documentId.toString());
        }
    }, [open, documentId]);

    const effectiveDocId = documentId ? documentId.toString() : selectedDocumentId;
    const canSubmit = !!ticketDetails.trim() && !!effectiveDocId && !!activeApp && !submitting;

    const handleClose = () => {
        setSelectedDocumentId('');
        setTicketDetails('');
        onClose();
    };

    const submit = async (): Promise<boolean> => {
        if (!effectiveDocId || !activeApp) return false;
        const document = docsList.find((doc: AppDocument) => doc.id.toString() === effectiveDocId);
        if (!document) return false;
        setSubmitting(true);
        try {
            await api.tickets.createTicket({
                document_id: document.id,
                text: ticketDetails,
            });
            localStorage.setItem('newTicket', 'yes');
            dispatchEvent(new Event('storageValueChanged'));
            dispatch(loadDocs(activeApp.id.toString()));
            onCreated?.();
            return true;
        } catch (e) {
            console.error(e);
            return false;
        } finally {
            setSubmitting(false);
        }
    };

    const title = t('createTicket', { lng: language });
    const subtitle = t('createTicketModalSubtitle', {
        lng: language,
        defaultValue: 'Link this request to a document and notify the team.',
    });

    const documentLabel = mobile
        ? t('document', { lng: language })
        : t('tagToDocument', { lng: language });

    return (
        <Modal open={open} onClose={handleClose} size="md" zIndex={1400} ariaLabel={title}>
            <Form
                onSubmit={async (event) => {
                    event.preventDefault();
                    const ok = await submit();
                    if (ok) {
                        handleClose();
                    }
                }}
            >
                <ModalHeader
                    eyebrow={t('tickets', { lng: language })}
                    title={title}
                    subtitle={subtitle}
                />
                <Section>
                    <Box sx={formAreaSx}>
                        <Stack spacing={2}>
                            {documentId ? (
                                <Box
                                    sx={{
                                        padding: '14px 16px',
                                        borderRadius: '14px',
                                        border: '1px solid rgba(148, 163, 184, 0.28)',
                                        background: 'rgba(248, 250, 252, 0.9)',
                                    }}
                                >
                                    <Typography
                                        component="div"
                                        sx={{
                                            fontSize: '11px',
                                            fontWeight: 700,
                                            letterSpacing: '0.12em',
                                            textTransform: 'uppercase',
                                            color: '#6d41d8',
                                            marginBottom: '4px',
                                        }}
                                    >
                                        {documentLabel}
                                    </Typography>
                                    <Typography
                                        component="div"
                                        sx={{
                                            fontSize: '15px',
                                            fontWeight: 600,
                                            color: '#111827',
                                            lineHeight: 1.4,
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {lockedDocName}
                                    </Typography>
                                </Box>
                            ) : (
                                <TextField
                                    select
                                    required
                                    fullWidth
                                    size="small"
                                    label={documentLabel}
                                    value={selectedDocumentId}
                                    onChange={(event) => setSelectedDocumentId(event.target.value)}
                                    SelectProps={{ MenuProps: longOptionsMenuProps }}
                                >
                                    {docsList.length === 0 ? (
                                        <MenuItem value="" disabled>
                                            {t('theListIsEmpty', { lng: language })}
                                        </MenuItem>
                                    ) : (
                                        docsList.map((doc: AppDocument) => (
                                            <MenuItem key={doc.id} value={doc.id.toString()}>
                                                {doc.document_name}
                                            </MenuItem>
                                        ))
                                    )}
                                </TextField>
                            )}
                            <TextField
                                required
                                fullWidth
                                size="small"
                                multiline
                                minRows={3}
                                label={t('ticketDetails', { lng: language })}
                                value={ticketDetails}
                                onChange={(event) => setTicketDetails(event.target.value)}
                            />
                        </Stack>
                    </Box>
                </Section>
                <Actions>
                    <SecondaryButton type="button" onClick={handleClose}>
                        {t('cancel', { lng: language })}
                    </SecondaryButton>
                    <PrimaryButton type="submit" disabled={!canSubmit}>
                        {submitting
                            ? t('creating', { lng: language })
                            : t('createTicketAndNotify', { lng: language })}
                    </PrimaryButton>
                </Actions>
            </Form>
        </Modal>
    );
}
